import { Button } from '@/components/form/Button';
import { ButtonVariant } from '@/components/form/buttonHelper';
import { Loading } from '@/components/loading/Loading';
import { Modal } from '@/components/modal/Modal';
import { ModalProps } from '@/components/modal/useModal';
import { Card } from '@/components/sections/Card';
import { useDeleteProductFilter, useProductFilter } from '@/services/api/hooks/catalog';

import { FunctionComponent, Suspense } from 'react';
import { toast } from 'sonner';
import { Criteria, FilterIcon } from './ManageFilters';

const DeleteProductFilterModal: FunctionComponent<
  ModalProps<{ productFilterId: string; filteredByManufacturer?: boolean }>
> = props => {
  const DeleteProductFilter = () => {
    const productFilterId = props.props?.productFilterId;
    if (!productFilterId) throw new Error('Missing product filter id');

    const [filter] = useProductFilter(productFilterId);

    const deleteFilter = useDeleteProductFilter();

    const onDelete = async () => {
      await deleteFilter.mutateAsync({ id: productFilterId });
      props.close();
      toast.success(`Filter deleted`);
    };

    const errorObj = deleteFilter.error as Error | undefined;
    const error: string = errorObj ? errorObj.message : '';

    return (
      <Card className="w-full p-8">
        <Modal.Controls {...props}>
          <h1 className="text-2xl font-semibold">Delete filter</h1>
        </Modal.Controls>

        <div className="space-y-3 mt-3">
          <div>
            <p className="text-lg">Are you sure you want to delete:</p>
            <div className="flex items-center">
              <FilterIcon />
              <Criteria
                label={props.props?.filteredByManufacturer ? 'Manufacturer' : 'Category'}
                value={props.props?.filteredByManufacturer ? filter.manufacturer!.name! : filter.category!.name!}
              />
            </div>
          </div>

          {error && <p className="text-red-500">{error}</p>}

          <div className="flex gap-2 justify-end">
            <Button className="flex-1" onClick={props.close}>
              Cancel
            </Button>
            <Button
              type="button"
              className="flex-1"
              variant={ButtonVariant.Destructive}
              loading={deleteFilter.isLoading}
              onClick={onDelete}
            >
              Delete
            </Button>
          </div>
        </div>
      </Card>
    );
  };

  return (
    <Modal className="md:max-w-xl" {...props}>
      <Suspense fallback={<Loading />}>
        <DeleteProductFilter />
      </Suspense>
    </Modal>
  );
};

export default DeleteProductFilterModal;
